// ────────────────────────────────────────────────────────────────────────────
//  Landing.tsx — Marketing-Seite im CustoSoft-Glas-Look
//
//  Sektionen (in Reihenfolge):
//   1. Floating-Hero       — riesige Headline + 3D-Karten + "Live"-Pulse
//   2. PlatformShowcase    — iOS · iPad · Mac live · Webapp soon · Android später
//   3. FeatureGrid         — Glas-Karten der Hauptfeatures
//   4. RoadmapSection      — was läuft, was kommt, Status-Indikatoren
//   5. BetaTesterSection   — 100-Plätze Counter + Apply-Form + Lifetime-Reward
//   6. FinalCTA            — App-Store-Badge + Newsletter-Mini-Form
//   7. Footer              — Impressum verlinkt zur alten Seite
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  LATEST_RELEASE,
  APP_META as PATCH_APP_META,
  KIND_META as PATCH_KIND_META,
  KIND_ORDER as PATCH_KIND_ORDER,
  type PatchApp as _PatchApp,
} from '../data/patchNotes'
import { BackgroundOrbs } from '../components/BackgroundOrbs'

// ─────────────────────────────────────────────────────────────────────────────
// Apple-App-Store-URL — eine zentrale Konstante. Wenn Apple unter einer
// anderen ID veröffentlicht (z.B. nach Review-Approve), nur hier ändern.
// Format: /de/app/{slug}/id{numericId}  · Slug ist nur SEO, ID ist authoritativ.
// ─────────────────────────────────────────────────────────────────────────────
const APP_STORE_URL = 'https://apps.apple.com/de/app/custosoft/id6766051168'
import { API_ORIGIN } from '../lib/api'
import { useAuth } from '../lib/useAuth'
import { loginWithEmail } from '../lib/auth'
import { loginWithApple, isAppleSignInConfigured } from '../lib/appleSignIn'

export function Landing() {
  return (
    <div className="relative min-h-full">
      <BackgroundOrbs />
      <Header />
      <Hero />
      <ReleaseBanner />
      <PunchClockTrialOffer />
      <PlatformShowcase />
      <FeatureGrid />
      <PatchNotesPreview />
      <ServerTeaser />
      <RoadmapSection />
      <BetaTesterSection />
      <FinalCTA />
      <Footer />
    </div>
  )
}

// ── HEADER ────────────────────────────────────────────────────────────────
function Header() {
  const { user } = useAuth()
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-app-bg/60 border-b border-app-border">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5 group shrink-0">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent to-accent2 grid place-items-center font-bold text-white shadow-lg shadow-accent/30">C</span>
          <span className="font-bold text-lg tracking-tight text-app-text">CustoSoft</span>
        </a>
        <nav className="hidden md:flex items-center gap-6 text-sm text-app-text-dim">
          <a href="#features"   className="hover:text-app-text transition">Features</a>
          <a href="#patchnotes" className="hover:text-app-text transition">Patchnotes</a>
          <a href="#roadmap"    className="hover:text-app-text transition">Roadmap</a>
          <a href="#beta"       className="hover:text-app-text transition">Beta</a>
        </nav>
        {/* Wenn eingeloggt: Avatar-Pill mit Direkt-Link ins App-Dashboard. */}
        {/* Wenn nicht eingeloggt: Inline-Login-Dropdown — kein Seitenwechsel. */}
        {user ? <SignedInPill /> : <SignInDropdown />}
      </div>
    </header>
  )
}

// ── Avatar-Pill (eingeloggt) ───────────────────────────────────────────────
function SignedInPill() {
  const { user } = useAuth()
  if (!user) return null
  const initials = (user.displayName || user.email || '?')
    .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
  return (
    <Link
      to="/app"
      className="flex items-center gap-2.5 pl-1.5 pr-3 py-1 rounded-full bg-app-elev border border-app-border-2 hover:bg-app-elev-2 hover:border-accent/40 transition group"
      title="Zum Dashboard"
    >
      {user.avatarUrl ? (
        <img
          src={user.avatarUrl.startsWith('http') ? user.avatarUrl : `${API_ORIGIN}${user.avatarUrl}`}
          alt=""
          width={28} height={28}
          className="rounded-full object-cover"
        />
      ) : (
        <span className="w-7 h-7 rounded-full grid place-items-center text-xs font-bold text-white bg-gradient-to-br from-accent to-accent2">
          {initials || '?'}
        </span>
      )}
      <span className="hidden sm:block text-sm font-medium text-app-text max-w-[120px] truncate">
        {firstNameOf(user.displayName) || 'Account'}
      </span>
      <span className="text-xs text-app-text-mute group-hover:text-accent transition hidden sm:block">→</span>
    </Link>
  )
}

function firstNameOf(s?: string | null) {
  return (s ?? '').trim().split(/\s+/)[0] ?? ''
}

// ── Sign-In Dropdown (nicht eingeloggt) ────────────────────────────────────
function SignInDropdown() {
  const { setAuthUser } = useAuth()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy]         = useState(false)
  const [appleBusy, setAppleBusy] = useState(false)
  const [err, setErr]           = useState<string | null>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      const u = await loginWithEmail(email.trim(), password)
      setAuthUser(u)
      setOpen(false)
      nav('/app', { replace: true })
    } catch (e: any) {
      setErr(e?.message ?? 'Login fehlgeschlagen.')
    } finally { setBusy(false) }
  }

  const apple = async () => {
    setAppleBusy(true); setErr(null)
    try {
      const u = await loginWithApple()
      setAuthUser(u)
      setOpen(false)
      nav('/app', { replace: true })
    } catch (e: any) {
      const msg = e?.message ?? ''
      if (!/popup_closed|popup-closed|cancel/i.test(msg)) setErr(msg)
    } finally { setAppleBusy(false) }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`text-sm px-4 py-2 rounded-xl border transition ${
          open
            ? 'bg-app-elev-2 border-app-border-2 text-app-text'
            : 'bg-app-elev border-app-border hover:bg-app-elev-2 text-app-text'
        }`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        Anmelden
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[320px] rounded-2xl glass-strong p-5 z-50 animate-fade-in-up"
          role="dialog"
        >
          <div className="text-base font-bold mb-1 text-app-text">Willkommen zurück</div>
          <div className="text-xs text-app-text-dim mb-4">Mit deinem CustoSoft-Account weiter.</div>

          {/* Apple */}
          {isAppleSignInConfigured ? (
            <button
              type="button"
              onClick={apple}
              disabled={appleBusy || busy}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-black text-white font-semibold text-sm hover:bg-black/85 transition disabled:opacity-50"
            >
              {appleBusy ? 'Apple öffnet…' : ' Mit Apple anmelden'}
            </button>
          ) : (
            <div className="w-full px-4 py-2.5 rounded-xl bg-app-elev border border-app-border text-xs text-app-text-mute text-center">
               Mit Apple anmelden ·
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/20 border border-accent/30 text-accent font-bold tracking-wider ml-1">BALD</span>
            </div>
          )}

          {/* Trenner */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-app-border" />
            <span className="text-[10px] font-bold text-app-text-mute tracking-wider">ODER</span>
            <div className="flex-1 h-px bg-app-border" />
          </div>

          <form onSubmit={submit} className="space-y-2.5">
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="E-Mail"
              autoComplete="email"
              className="input-base text-sm"
            />
            <input
              type="password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Passwort"
              autoComplete="current-password"
              className="input-base text-sm"
            />
            {err && <div className="text-xs text-coral bg-coral/10 border border-coral/30 rounded-lg p-2">{err}</div>}
            <button
              type="submit"
              disabled={busy || appleBusy || !email || !password}
              className="btn-primary w-full text-sm py-2.5 disabled:opacity-50"
            >
              {busy ? 'Anmelden…' : 'Anmelden'}
            </button>
          </form>

          <div className="mt-3 text-center">
            <Link to="/login" onClick={() => setOpen(false)} className="text-xs text-app-text-mute hover:text-app-text transition">
              Vollständige Anmelde-Seite öffnen →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

// ── HERO ──────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section id="top" className="relative px-6 pt-20 md:pt-32 pb-24 max-w-6xl mx-auto">
      {/* Live-Badge mit Conic-Gradient-Pulse */}
      <div className="flex justify-center mb-8">
        <div className="conic-border rounded-full p-[1px]">
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-app-bg/90 text-xs font-bold tracking-wider">
            <span className="w-2 h-2 rounded-full bg-teal animate-pulse-glow" />
            <span className="shimmer-text">iOS v1.1 LIVE · PRISM SKIN IM SHOP · STEMPELUHR 30 TAGE GRATIS · MAC + IPAD + IPHONE SYNC</span>
          </div>
        </div>
      </div>

      {/* Riesen-Headline */}
      <h1 className="text-center text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight">
        <span className="heading-fade">
          Dein Team.
        </span>
        <br />
        <span className="heading-brand">
          Eine App.
        </span>
        <br />
        <span className="heading-fade-soft">
          Komplett Apple.
        </span>
      </h1>

      <p className="mt-8 text-center text-lg md:text-xl text-app-text-dim max-w-2xl mx-auto leading-relaxed">
        Stempeluhr, Team-Chat, Mitarbeiterakten, Bewerbungsmanager.<br className="hidden md:block" />
        Liquid-Glass-Design, Dynamic Island, Live Activity. Made in Germany.
      </p>

      {/* CTA-Reihe */}
      <div className="mt-12 flex flex-wrap justify-center gap-4">
        <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="btn-primary group">
          <AppleLogo />
          Im App Store laden
        </a>
        <a href="#beta" className="btn-ghost">
          Beta-Tester werden
          <span className="text-accent">→</span>
        </a>
      </div>

      {/* 3D-Floating-Karten */}
      <FloatingCards />

      {/* Stats-Bar */}
      <div className="mt-20 grid grid-cols-3 gap-2 md:gap-6 max-w-3xl mx-auto">
        <StatTile value="3" label="Apple Plattformen live" />
        <StatTile value="34" label="Sprachen unterstützt" />
        <StatTile value="100" label="Beta-Tester-Slots" highlight />
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// RELEASE-CELEBRATION-BANNER (v1.1 LIVE im App Store)
//
// Eigene Section, kommt direkt nach dem Hero und vor PlatformShowcase. Voll-
// flächige Bühne mit Confetti, Ring-Burst, animierten Sparkles und dem
// Prism-Teaser. Feiert das v1.1-Update (Redesign + Prism-Skin) das jetzt
// live im App Store ist.
// ─────────────────────────────────────────────────────────────────────────────
function ReleaseBanner() {
  // Confetti-Stücke werden einmal beim Mount generiert, danach laufen sie
  // mit reiner CSS-Animation (keine setState pro Frame → null CPU-Last).
  const confetti = useMemo(() => {
    const colors = ['#8a44ee', '#3a62ff', '#33d670', '#ffaa3d', '#ff5b5c', '#ffffff']
    return Array.from({ length: 36 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 6 + Math.random() * 6,
      color: colors[i % colors.length],
      rotate: Math.random() * 360,
    }))
  }, [])

  // Sparkles um die Version-Nummer
  const sparkles = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      top: 20 + Math.random() * 60,
      left: 5 + Math.random() * 90,
      delay: Math.random() * 2,
      size: 8 + Math.random() * 8,
    }))
  }, [])

  return (
    <section className="relative overflow-hidden px-6 py-20 md:py-28">
      {/* Background-Glow Layer (rotating gradient) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full blur-3xl release-glow"
             style={{
               background: 'radial-gradient(circle, rgba(138,68,238,0.45) 0%, rgba(58,98,255,0.20) 35%, transparent 70%)',
             }} />
      </div>

      {/* Confetti rain — fixed-Position-Container, klippt aufs Section-Rect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {confetti.map(c => (
          <span
            key={c.id}
            className="confetti-piece"
            style={{
              left:               `${c.left}%`,
              background:         c.color,
              animationDelay:     `${c.delay}s`,
              animationDuration:  `${c.duration}s`,
              transform:          `rotate(${c.rotate}deg)`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* ── HERO der Section: Versions-Counter + Hauptmessage ───────────── */}
        <div className="text-center mb-16 relative">
          {/* Ring-Bursts um die Versions-Zahl */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="ring-burst" style={{ width: 240, height: 240, left: -120, top: -120, animationDelay: '0s' }} />
            <div className="ring-burst" style={{ width: 240, height: 240, left: -120, top: -120, animationDelay: '0.7s' }} />
            <div className="ring-burst" style={{ width: 240, height: 240, left: -120, top: -120, animationDelay: '1.4s' }} />
          </div>

          {/* Sparkles um die Version */}
          {sparkles.map(s => (
            <span
              key={s.id}
              className="sparkle"
              style={{
                top:              `${s.top}%`,
                left:             `${s.left}%`,
                width:            `${s.size}px`,
                height:           `${s.size}px`,
                animationDelay:   `${s.delay}s`,
                background:       'radial-gradient(circle, #fff 0%, #ffaa3d 50%, transparent 70%)',
                borderRadius:     '50%',
              }}
            />
          ))}

          {/* Mini-Kicker */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
               style={{
                 background: 'rgba(51,214,112,0.18)',
                 border: '1px solid rgba(51,214,112,0.45)',
                 color: '#33d670',
               }}>
            <span className="w-2 h-2 rounded-full bg-[#33d670] animate-pulse" />
            <span className="text-xs font-bold tracking-widest">JETZT IM APP STORE</span>
          </div>

          {/* Big Version Number — Pop-In */}
          <div className="version-pop relative inline-block">
            <h2 className="text-7xl md:text-9xl font-black tracking-tighter leading-none">
              <span className="gradient-sweep">v1.1</span>
            </h2>
            <div className="absolute -top-4 -right-12 md:-right-16 px-3 py-1 rounded-full text-[10px] font-black tracking-widest text-white shadow-lg"
                 style={{
                   background: 'linear-gradient(135deg, #33d670, #1a9d4f)',
                   transform: 'rotate(8deg)',
                 }}>
              LIVE
            </div>
          </div>

          <h3 className="mt-6 text-3xl md:text-5xl font-bold heading-fade leading-tight max-w-3xl mx-auto">
            v1.1 ist da.<br />
            <span className="heading-brand">Frisches Design, Prism-Skin, schnellere App.</span>
          </h3>

          <p className="mt-6 text-base md:text-lg text-app-text-dim max-w-2xl mx-auto leading-relaxed">
            Das bisher größte Update ist live im App Store.
            <strong className="text-app-text"> Komplettes Redesign aller Hauptscreens</strong>,
            neuer <strong className="text-app-text">„Prism"-Cross-Platform-Skin</strong> im Shop,
            zuverlässigere Käufe nach App-Updates und 2× schneller geladene Stempel-Aktionen.
            Bestehende Nutzer bekommen das Update direkt aufs Gerät — neue Nutzer laden frisch.
          </p>

          {/* App Store CTA */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-white text-lg mega-glow shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #8a44ee, #3a62ff)',
                boxShadow: '0 20px 60px -10px rgba(138,68,238,0.50)',
              }}
            >
              <AppleLogo />
              <span>Im App Store laden</span>
              <span className="text-sm font-normal opacity-80">v1.1</span>
            </a>
            <a
              href="#patchnotes"
              className="text-sm text-app-text-dim hover:text-app-text underline-offset-4 hover:underline transition"
            >
              Was ist neu? →
            </a>
          </div>

          {/* Platform-Pills */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {[
              { name: 'iPhone', live: true },
              { name: 'iPad',   live: true },
              { name: 'Mac',    live: false },   // ⏳ Apple-Review läuft
            ].map((p, i) => (
              <div key={p.name}
                   className="px-3.5 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 backdrop-blur-md"
                   style={{
                     background: 'rgba(255,255,255,0.06)',
                     border: '1px solid rgba(255,255,255,0.14)',
                     animationDelay: `${i * 100}ms`,
                   }}>
                <span className={p.live ? 'w-1.5 h-1.5 rounded-full bg-[#33d670]' : 'w-1.5 h-1.5 rounded-full bg-[#ffaa3d] animate-pulse'} />
                <span className="text-app-text">{p.name}</span>
                <span className={p.live ? 'text-app-text-mute' : 'text-[#ffaa3d]'}>
                  {p.live ? 'supported' : 'in Prüfung'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── v1.1 Live-Teaser-Karte (Prism Showcase) ─────────────────────── */}
        <Vorone11Teaser />
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// v1.1 TEASER-KARTE
// Zeigt das Headline-Feature von v1.1: Cross-Platform Skin „Prism".
// Seit 12.05.2026 live im App Store — Badge auf grün, Progress 100%,
// CTA direkt zum App-Store-Listing.
// ─────────────────────────────────────────────────────────────────────────────
function Vorone11Teaser() {
  return (
    <div className="relative mt-12 max-w-5xl mx-auto">
      <div className="absolute -inset-2 rounded-3xl opacity-50 blur-2xl"
           style={{ background: 'linear-gradient(135deg, rgba(51,214,112,0.30), rgba(58,98,255,0.20), rgba(138,68,238,0.20))' }} />
      <div className="relative rounded-3xl p-7 md:p-10 grid md:grid-cols-2 gap-8 items-center"
           style={{
             background: 'linear-gradient(135deg, rgba(20,15,30,0.85), rgba(15,12,25,0.95))',
             border: '1px solid rgba(51,214,112,0.30)',
             backdropFilter: 'blur(20px)',
           }}>

        {/* Left: Text + Release-Status */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest mb-4"
               style={{
                 background: 'rgba(51,214,112,0.20)',
                 border: '1px solid rgba(51,214,112,0.45)',
                 color: '#33d670',
               }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#33d670] animate-pulse" />
            JETZT LIVE · v1.1
          </div>

          <h3 className="text-3xl md:text-4xl font-bold heading-fade leading-tight">
            <span className="heading-brand">Prism.</span><br />
            Ein Skin, alle Welten.
          </h3>

          <p className="mt-4 text-sm md:text-base text-app-text-dim leading-relaxed">
            v1.1 ist live. <strong className="text-app-text">Prism</strong> ist der Cross-Platform-Skin
            der die App je Device perfekt einkleidet: auf
            <strong className="text-app-text"> iPhone</strong> Material 3 mit Custom-Icons,
            Bottom-Nav-Pill und FAB. Auf
            <strong className="text-app-text"> iPad und Mac</strong> Fluent-Window-Style
            mit Title-Bar, Explorer-Sidebar und Mica-Background.
            Jedes Menü, jedes Detail. <strong className="text-app-text">Im Shop für 4,99 € — einmal kaufen, auf allen Geräten aktiv.</strong>
          </p>

          {/* Release-Status — alle drei Stages erledigt */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-app-text-mute font-semibold tracking-wider">RELEASE-STATUS</span>
              <span className="font-bold text-[#33d670]">VERÖFFENTLICHT</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full"
                   style={{
                     width: '100%',
                     background: 'linear-gradient(90deg, #8a44ee, #3a62ff, #33d670)',
                   }} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-app-text-mute">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#33d670]" />
                Code fertig
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#33d670]" />
                Tests grün
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#33d670]" />
                App-Review freigegeben
              </span>
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#33d670]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#33d670] animate-pulse" />
                Live seit 12. Mai 2026
              </span>
            </div>
          </div>

          {/* CTA — direkt zum App Store */}
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #33d670, #3a62ff)',
                color: '#0a0a0f',
                boxShadow: '0 4px 20px rgba(51,214,112,0.35)',
              }}>
              Im App Store öffnen
              <span aria-hidden>→</span>
            </a>
            <a
              href="/patchnotes"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition hover:bg-white/5"
              style={{
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'var(--app-text)',
              }}>
              Was ist noch neu?
            </a>
          </div>

          {/* Mini-Specs */}
          <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-start gap-2">
              <span className="text-base">📱</span>
              <div>
                <div className="font-bold text-app-text">iPhone</div>
                <div className="text-app-text-mute">Material 3 · M3-Icons · BottomNav-Pill</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-base">🖥️</span>
              <div>
                <div className="font-bold text-app-text">iPad &amp; Mac</div>
                <div className="text-app-text-mute">Fluent-Window · TitleBar · Sidebar</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Mini-Skin-Preview (zwei animierte Devices Side-by-Side) */}
        <div className="relative flex justify-center items-center min-h-[280px]">
          <SkinPreviewIPhone />
          <SkinPreviewMac />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PUNCH-CLOCK 30-TAGE-GRATIS-AKTION
//
// Launch-Promo nur für die Apple-Version. Nur für NEUE Nutzer (kein
// Re-Trial wenn der User bereits gekauft + gekündigt hat — Apple regelt
// das eligibility per Apple-ID-Subscription-History).
// ─────────────────────────────────────────────────────────────────────────────
function PunchClockTrialOffer() {
  return (
    <section className="relative overflow-hidden px-6 py-14 md:py-20">
      {/* Layered Background: subtler Green-Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full blur-3xl"
             style={{
               background: 'radial-gradient(ellipse, rgba(51,214,112,0.30) 0%, rgba(0,180,140,0.10) 40%, transparent 70%)',
             }} />
      </div>

      <div className="relative max-w-5xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden"
             style={{
               background: 'linear-gradient(135deg, rgba(51,214,112,0.10), rgba(0,180,140,0.05), rgba(20,15,30,0.85))',
               border: '2px solid rgba(51,214,112,0.35)',
               backdropFilter: 'blur(20px)',
             }}>

          {/* Floating Sparkle-Dots im Card-Hintergrund */}
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 8 }, (_, i) => (
              <span
                key={i}
                className="sparkle"
                style={{
                  top:               `${10 + (i * 11) % 80}%`,
                  left:              `${5 + (i * 17) % 90}%`,
                  width:             '10px',
                  height:            '10px',
                  animationDelay:    `${i * 0.3}s`,
                  background:        'radial-gradient(circle, #ffffff 0%, #33d670 60%, transparent 80%)',
                  borderRadius:      '50%',
                }}
              />
            ))}
          </div>

          {/* Animiertes Uhr-Ring oben rechts als Watermark */}
          <div className="absolute -top-10 -right-10 opacity-20 pointer-events-none">
            <AnimatedClockWatermark />
          </div>

          <div className="relative p-7 md:p-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
            {/* Left: Big Promo-Headline */}
            <div>
              {/* Kicker mit Pulse */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                   style={{
                     background: 'rgba(51,214,112,0.20)',
                     border: '1px solid rgba(51,214,112,0.50)',
                   }}>
                <span className="w-2 h-2 rounded-full bg-[#33d670] animate-pulse" />
                <span className="text-[11px] font-black tracking-widest text-[#33d670]">
                  LAUNCH-AKTION · NUR APPLE · NUR FÜR NEUE NUTZER
                </span>
              </div>

              <h3 className="text-4xl md:text-6xl font-black leading-[1.05] tracking-tight">
                <span className="block heading-fade">Stempeluhr.</span>
                <span className="block text-transparent bg-clip-text"
                      style={{ background: 'linear-gradient(135deg, #33d670, #00d4aa)', WebkitBackgroundClip: 'text', backgroundClip: 'text' }}>
                  30 Tage komplett gratis.
                </span>
              </h3>

              <p className="mt-5 text-base md:text-lg text-app-text-dim leading-relaxed max-w-xl">
                Lade dir die App herunter, melde dich an, aktivier die Stempeluhr —
                <strong className="text-app-text"> der erste Monat ist auf uns</strong>.
                Erst danach 3,99 €/Monat. Jederzeit kündbar.
              </p>

              {/* Conditions als Pills */}
              <div className="mt-5 flex flex-wrap gap-2">
                <ConditionPill icon="🍎" label="Nur in der Apple-App" />
                <ConditionPill icon="✨" label="Nur für neue Nutzer" />
                <ConditionPill icon="🔄" label="Jederzeit kündbar" />
                <ConditionPill icon="🚫" label="Keine Kreditkarte voraus nötig" />
              </div>

              {/* CTAs */}
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-2xl font-bold text-white text-base shadow-2xl mega-glow"
                  style={{
                    background: 'linear-gradient(135deg, #33d670, #00b48c)',
                    boxShadow: '0 18px 50px -12px rgba(51,214,112,0.55)',
                  }}
                >
                  <AppleLogo />
                  <span>Im App Store laden</span>
                </a>
                <span className="text-xs text-app-text-mute">
                  Aktion endet automatisch nach 30 Tagen — danach 3,99 €/Monat
                </span>
              </div>
            </div>

            {/* Right: Big „30" mit animierten Strahlen */}
            <div className="relative flex items-center justify-center min-w-[180px] min-h-[180px]">
              {/* Strahlen-Kranz */}
              <div className="absolute inset-0 animate-spin-slow opacity-50 pointer-events-none">
                {Array.from({ length: 12 }, (_, i) => (
                  <div
                    key={i}
                    className="absolute top-1/2 left-1/2"
                    style={{
                      width: '2px',
                      height: '80px',
                      background: 'linear-gradient(to top, transparent, rgba(51,214,112,0.50))',
                      transformOrigin: 'center top',
                      transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-50px)`,
                    }}
                  />
                ))}
              </div>
              {/* Pulsierender Kreis */}
              <div className="absolute w-[170px] h-[170px] rounded-full release-glow"
                   style={{
                     background: 'radial-gradient(circle, rgba(51,214,112,0.40) 0%, transparent 70%)',
                   }} />
              <div className="relative flex flex-col items-center">
                <div className="text-[120px] md:text-[160px] font-black leading-none version-pop"
                     style={{
                       background: 'linear-gradient(135deg, #33d670, #00d4aa, #4ce6c1)',
                       WebkitBackgroundClip: 'text',
                       backgroundClip: 'text',
                       color: 'transparent',
                       textShadow: '0 8px 30px rgba(51,214,112,0.30)',
                     }}>
                  30
                </div>
                <div className="text-sm font-black tracking-[0.30em] text-[#33d670] -mt-2">
                  TAGE GRATIS
                </div>
              </div>
            </div>
          </div>

          {/* Bottom-Strip mit Fine-Print */}
          <div className="px-6 md:px-10 py-3 text-[11px] text-app-text-mute leading-relaxed border-t"
               style={{ borderColor: 'rgba(51,214,112,0.18)', background: 'rgba(0,0,0,0.20)' }}>
            <strong className="text-app-text-dim">Hinweis:</strong> Der 30-Tage-Gratis-Zeitraum
            wird von Apple verwaltet. Eligibility (= Anspruch auf den kostenlosen Zeitraum) prüft
            Apple anhand der Subscription-Historie deiner Apple-ID — User die bereits ein
            Stempeluhr-Abo abgeschlossen oder beendet haben, fallen direkt in den 3,99 €/Monat-
            Modus. Die Aktion gilt ausschließlich in der iOS-/iPadOS-/macOS-App.
          </div>
        </div>
      </div>
    </section>
  )
}

function ConditionPill({ icon, label }: { icon: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.10)',
            color: 'rgba(255,255,255,0.90)',
          }}>
      <span>{icon}</span>{label}
    </span>
  )
}

// Animierter Watermark-Clock-Ring rechts oben in der Promo-Karte
function AnimatedClockWatermark() {
  return (
    <div className="relative w-[180px] h-[180px]">
      <div className="absolute inset-0 rounded-full border-[3px]"
           style={{ borderColor: 'rgba(51,214,112,0.50)' }} />
      <div className="absolute inset-4 rounded-full border-[2px]"
           style={{ borderColor: 'rgba(51,214,112,0.35)' }} />
      {/* Sekunden-Zeiger */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full origin-bottom animate-spin-slow"
           style={{
             width: '3px',
             height: '70px',
             background: 'linear-gradient(to top, #33d670, transparent)',
             borderRadius: '2px',
           }} />
      {/* Minuten-Marker */}
      {Array.from({ length: 12 }, (_, i) => (
        <div key={i}
             className="absolute top-1/2 left-1/2"
             style={{
               width: '2px',
               height: '8px',
               background: '#33d670',
               opacity: 0.50,
               transformOrigin: 'center 80px',
               transform: `translate(-50%, -100%) rotate(${i * 30}deg)`,
             }} />
      ))}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#33d670]" />
    </div>
  )
}

// Mini-iPhone-Mockup mit Material 3 Look
function SkinPreviewIPhone() {
  return (
    <div className="relative phone-tilt z-10"
         style={{ transform: 'perspective(1200px) rotateY(-8deg)' }}>
      <div className="w-[110px] h-[220px] rounded-[22px] p-1.5 shadow-2xl"
           style={{
             background: 'linear-gradient(135deg, #2a2a2a, #1a1a1a)',
             boxShadow: '0 30px 60px -10px rgba(0,0,0,0.7)',
           }}>
        <div className="w-full h-full rounded-[18px] overflow-hidden flex flex-col"
             style={{ background: '#FEFBFF' }}>
          {/* App Bar Material */}
          <div className="px-2 py-2 flex items-center justify-between"
               style={{ background: '#F6F2F8' }}>
            <div className="text-[8px] font-semibold" style={{ color: '#1A1729' }}>CustoSoft</div>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#6750A4' }} />
          </div>
          {/* Content */}
          <div className="flex-1 p-2 space-y-1.5">
            <div className="h-3 rounded-full" style={{ background: '#E8DEF8' }} />
            <div className="h-2 rounded-full w-2/3" style={{ background: '#E8DEF8', opacity: 0.7 }} />
            <div className="mt-3 h-10 rounded-lg" style={{ background: 'linear-gradient(135deg, #6750A4, #7F67BE)' }} />
            <div className="h-2 rounded-full" style={{ background: '#E8DEF8', opacity: 0.7 }} />
            <div className="h-2 rounded-full w-3/4" style={{ background: '#E8DEF8', opacity: 0.7 }} />
          </div>
          {/* Material Bottom Nav */}
          <div className="flex items-end justify-around px-1 pb-1.5 pt-1"
               style={{ background: '#F6F2F8' }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="flex flex-col items-center gap-0.5">
                {i === 1 ? (
                  <div className="px-2 py-0.5 rounded-full" style={{ background: '#E8DEF8' }}>
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#6750A4' }} />
                  </div>
                ) : (
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#1A1729', opacity: 0.6 }} />
                )}
                <div className="w-3 h-0.5 rounded" style={{ background: i === 1 ? '#6750A4' : '#1A1729', opacity: i === 1 ? 1 : 0.6 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest"
           style={{ color: '#6750A4' }}>
        MATERIAL 3
      </div>
    </div>
  )
}

// Mini-Mac/iPad-Mockup mit Windows 11 Look
function SkinPreviewMac() {
  return (
    <div className="relative phone-tilt ml-[-30px] z-0"
         style={{
           animationDelay: '-3s',
           transform: 'perspective(1200px) rotateY(10deg)',
         }}>
      <div className="w-[180px] h-[140px] rounded-md shadow-2xl overflow-hidden"
           style={{
             background: '#202020',
             boxShadow: '0 30px 60px -10px rgba(0,0,0,0.7)',
             border: '1px solid rgba(255,255,255,0.08)',
           }}>
        {/* Windows Title Bar */}
        <div className="px-2 py-1 flex items-center justify-between" style={{ background: '#1A1A1A' }}>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ background: '#4CC2FF' }} />
            <div className="text-[7px] font-semibold text-white">CustoSoft</div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-[7px] text-white opacity-70">_</div>
            <div className="w-1 h-1 border border-white/70" />
            <div className="text-[7px] text-white">✕</div>
          </div>
        </div>
        {/* Menu Bar */}
        <div className="px-2 py-0.5 flex items-center gap-2" style={{ background: '#1A1A1A', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {['Datei', 'Bearb.', 'Ansicht'].map(t => (
            <div key={t} className="text-[6px] text-white opacity-75">{t}</div>
          ))}
        </div>
        {/* Content + Sidebar */}
        <div className="flex h-[100px]">
          <div className="w-[40px] p-1 space-y-1" style={{ background: '#2B2B2B' }}>
            {[true, false, false, false].map((sel, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-0.5 h-2.5 rounded" style={{ background: sel ? '#4CC2FF' : 'transparent' }} />
                <div className="w-2 h-2 rounded-sm" style={{ background: sel ? '#4CC2FF' : 'white', opacity: sel ? 1 : 0.6 }} />
              </div>
            ))}
          </div>
          <div className="flex-1 p-1.5 space-y-1" style={{ background: '#202020' }}>
            <div className="h-2 rounded-sm" style={{ background: 'rgba(255,255,255,0.10)' }} />
            <div className="h-2 rounded-sm w-2/3" style={{ background: 'rgba(255,255,255,0.07)' }} />
            <div className="mt-1 h-5 rounded" style={{ background: 'linear-gradient(135deg, #4CC2FF, #38A0DB)' }} />
            <div className="h-1.5 rounded-sm w-3/4" style={{ background: 'rgba(255,255,255,0.07)' }} />
          </div>
        </div>
      </div>
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest whitespace-nowrap"
           style={{ color: '#4CC2FF' }}>
        WINDOWS 11
      </div>
    </div>
  )
}

function FloatingCards() {
  return (
    <div className="relative mt-16 mx-auto max-w-4xl h-[260px] md:h-[340px]">
      {/* Mittlere große Karte */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                   w-64 md:w-72 glass-strong p-6 animate-float-card z-20 tilt-hover"
        style={{ '--r': '0deg' } as React.CSSProperties}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent2 grid place-items-center text-white text-sm font-bold animate-pulse-glow">▶</div>
          <div>
            <div className="text-[10px] text-app-text-mute font-bold tracking-wider">EINGESTEMPELT</div>
            <div className="text-2xl font-bold font-mono tracking-tight text-app-text">06:42:17</div>
          </div>
        </div>
        <div className="h-1.5 rounded-full bg-app-border overflow-hidden">
          <div className="h-full w-3/4 bg-gradient-to-r from-teal to-brandBlue rounded-full" />
        </div>
        <div className="mt-3 flex justify-between text-[11px] text-app-text-mute font-medium">
          <span>seit 03:00</span>
          <span className="text-teal">▶ läuft</span>
        </div>
      </div>

      {/* Kleine Karte links */}
      <div
        className="hidden md:block absolute left-4 top-8 w-44 glass p-4 animate-float-card z-10 tilt-hover"
        style={{ '--r': '-8deg', animationDelay: '-2s' } as React.CSSProperties}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-teal/30 grid place-items-center text-teal font-bold text-sm">A</div>
          <div className="text-xs">
            <div className="font-bold text-app-text">Anna M.</div>
            <div className="text-app-text-mute text-[10px]">vor 3 min</div>
          </div>
        </div>
        <div className="text-xs text-app-text-dim leading-snug">Guten Morgen! Eingestempelt ✓</div>
      </div>

      {/* Kleine Karte rechts */}
      <div
        className="hidden md:block absolute right-4 top-12 w-48 glass p-4 animate-float-card z-10 tilt-hover"
        style={{ '--r': '6deg', animationDelay: '-4s' } as React.CSSProperties}
      >
        <div className="text-[10px] text-app-text-mute font-bold tracking-wider mb-2">DIESE WOCHE</div>
        <div className="flex items-end justify-between gap-1 h-12">
          {[0.7, 0.85, 0.95, 0.9, 0.75, 0.2, 0].map((h, i) => (
            <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-accent/60 to-accent" style={{ height: `${h * 100}%` }} />
          ))}
        </div>
        <div className="mt-2 text-sm font-bold text-app-text">32:15 h</div>
      </div>

      {/* Kleine Karte unten links */}
      <div
        className="hidden md:block absolute left-12 bottom-2 w-40 glass p-3 animate-float-card z-10 tilt-hover"
        style={{ '--r': '4deg', animationDelay: '-3s' } as React.CSSProperties}
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl">☕</span>
          <div className="text-xs">
            <div className="font-bold text-orange-500 dark:text-orange-300">Pause</div>
            <div className="text-app-text-mute text-[10px]">15:00</div>
          </div>
        </div>
      </div>

      {/* Kleine Karte unten rechts — Bewerbung */}
      <div
        className="hidden md:block absolute right-8 bottom-4 w-44 glass p-3 animate-float-card z-10 tilt-hover"
        style={{ '--r': '-5deg', animationDelay: '-1s' } as React.CSSProperties}
      >
        <div className="flex items-center gap-2 mb-1">
          <div className="w-7 h-7 rounded-full bg-coral/30 grid place-items-center text-coral text-xs font-bold">J</div>
          <div className="font-bold text-xs text-app-text">Jonas M.</div>
          <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-teal/20 text-teal rounded-md font-bold">NEU</span>
        </div>
        <div className="text-[11px] text-app-text-dim">Bewerbung Bäcker/in</div>
      </div>
    </div>
  )
}

function StatTile({ value, label, highlight = false }: { value: string; label: string; highlight?: boolean }) {
  return (
    <div className={`glass p-4 md:p-6 text-center ${highlight ? 'border-accent/40' : ''}`}>
      <div className={`text-3xl md:text-4xl font-bold ${highlight ? 'bg-gradient-to-br from-accent to-brandBlue bg-clip-text text-transparent' : 'text-app-text'}`}>
        {value}
      </div>
      <div className="mt-1 text-[10px] md:text-xs text-app-text-mute font-medium uppercase tracking-wider">{label}</div>
    </div>
  )
}

// ── PLATFORM SHOWCASE ────────────────────────────────────────────────────
function PlatformShowcase() {
  const platforms: { name: string; status: 'live' | 'review' | 'beta' | 'soon' | 'later'; icon: string }[] = [
    { name: 'iPhone',  status: 'live',   icon: '📱' },
    { name: 'iPad',    status: 'live',   icon: '🟦' },
    { name: 'Mac',     status: 'review', icon: '🖥' },
    { name: 'Webapp',  status: 'soon',   icon: '🌐' },
    { name: 'Android', status: 'later',  icon: '🤖' },
    { name: 'watchOS', status: 'later',  icon: '⌚' },
  ]
  const colors = {
    live:   { bg: 'bg-teal/15',     border: 'border-teal/40',      text: 'text-teal',          label: 'LIVE' },
    review: { bg: 'bg-[#ffaa3d]/15',border: 'border-[#ffaa3d]/40', text: 'text-[#ffaa3d]',     label: 'IN PRÜFUNG' },
    beta:   { bg: 'bg-brandBlue/15',border: 'border-brandBlue/40', text: 'text-brandBlue',     label: 'TESTFLIGHT' },
    soon:   { bg: 'bg-accent/15',   border: 'border-accent/40',    text: 'text-accent',        label: 'BALD' },
    later:  { bg: 'bg-app-elev',    border: 'border-app-border',   text: 'text-app-text-mute', label: 'GEPLANT' },
  } as const

  return (
    <section className="px-6 pb-24 max-w-6xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-block text-xs font-bold tracking-wider text-accent">PLATTFORMEN</div>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold heading-fade">Überall verfügbar.<br className="md:hidden"/> Eine Codebase.</h2>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
        {platforms.map(p => {
          const c = colors[p.status]
          return (
            <div key={p.name} className={`glass p-4 md:p-5 text-center tilt-hover`}>
              <div className="text-3xl md:text-4xl mb-2">{p.icon}</div>
              <div className="font-bold text-sm text-app-text">{p.name}</div>
              <div className={`mt-2 inline-block text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-full ${c.bg} ${c.border} ${c.text} border`}>
                {c.label}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ── FEATURE GRID ─────────────────────────────────────────────────────────
function FeatureGrid() {
  const features = [
    {
      icon: '🕐',
      title: 'Stempeluhr & Live Activity',
      text: 'Live-Timer in der Dynamic Island, Lock-Screen-Banner, Mac-Menüleisten-Icon. Dein Tag im Blick — auch außerhalb der App.',
      gradient: 'from-teal/20 to-brandBlue/20',
    },
    {
      icon: '💬',
      title: 'Team-Chat — Org-isoliert',
      text: 'Direkt-Chat und Gruppen, strikt getrennt nach Organisation. Push-Notifications, Datei-Anhänge, PDF-Vorschau.',
      gradient: 'from-accent/20 to-pink-400/20',
    },
    {
      icon: '📁',
      title: 'Mitarbeiterakten',
      text: 'Digitale Akten, Datei-Upload nach R2, Zeiterfassungs-CSV-Export pro Mitarbeiter — strikt getrennt.',
      gradient: 'from-brandBlue/20 to-accent/20',
    },
    {
      icon: '📋',
      title: 'Bewerbungsmanager',
      text: 'Öffentliche Stellen-Links erstellen, Bewerbungen sammeln, Workflow steuern — Anhänge inklusive.',
      gradient: 'from-coral/20 to-orange-400/20',
    },
    {
      icon: '🖥',
      title: 'Wand-Stempeluhr (iPad-Kiosk)',
      text: 'iPad an die Wand, alle Mitarbeiter stempeln per PIN. Vollbild-Modus, multi-User.',
      gradient: 'from-emerald-500/20 to-teal/20',
    },
    {
      icon: '🔒',
      title: 'DSGVO + EU-Hosting',
      text: 'Cloudflare EU-Region, Apple-Login mit Privacy-Relay, kein Tracking, keine Werbung. Made in Germany.',
      gradient: 'from-violet-500/20 to-accent/20',
    },
  ]

  return (
    <section id="features" className="px-6 pb-24 max-w-6xl mx-auto">
      <div className="text-center mb-14">
        <div className="inline-block text-xs font-bold tracking-wider text-accent">FEATURES</div>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold heading-fade">Alles was dein Team braucht.</h2>
        <p className="mt-3 text-app-text-dim max-w-xl mx-auto">Designed wie Apples eigene Apps. Bedient wie deine Lieblings-App.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map(f => (
          <div key={f.title} className="glass p-7 tilt-hover relative overflow-hidden">
            <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-50 bg-gradient-to-br ${f.gradient}`} />
            <div className="relative">
              <div className="text-4xl mb-4">{f.icon}</div>
              <h3 className="text-xl font-bold mb-2 text-app-text">{f.title}</h3>
              <p className="text-sm text-app-text-dim leading-relaxed">{f.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── PATCH-NOTES-TEASER ──────────────────────────────────────────────────
//  Slim card, die auf /patchnotes verlinkt. Volle Liste mit Filter +
//  Suche dort. Daten kommen aus src/data/patchNotes.ts.
function PatchNotesPreview() {
  const rel = LATEST_RELEASE
  const dateLocal = new Date(rel.date).toLocaleDateString('de-DE', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
  const kindCounts: Record<string, number> = { new: 0, changed: 0, fixed: 0, done: 0 }
  for (const e of rel.entries) kindCounts[e.kind]++
  const apps: _PatchApp[] = ['ios', 'webapp', 'backend']
  return (
    <section id="patchnotes" className="px-6 pb-24 max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-block text-xs font-bold tracking-wider text-accent">PATCHNOTES · AKTUELLER RELEASE</div>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold heading-fade">{rel.headline}</h2>
        <p className="mt-3 text-app-text-dim max-w-2xl mx-auto">{rel.summary}</p>
      </div>

      <div className="glass-strong p-6 md:p-8 rounded-3xl">
        <div className="flex flex-wrap items-start gap-4 mb-5 pb-5 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="shrink-0 flex flex-col items-center">
            <div className="text-2xl md:text-3xl font-bold tracking-tight">v{rel.version}</div>
            {rel.build && (
              <div className="text-[10px] font-bold tracking-widest text-app-text-mute mt-0.5">BUILD {rel.build}</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(255,179,0,0.16)', color: '#ffb300', border: '1px solid #ffb30055' }}>
                {rel.channel.toUpperCase()}
              </span>
              <span className="text-xs text-app-text-mute">{dateLocal}</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {PATCH_KIND_ORDER.map(k => kindCounts[k] > 0 && (
                <span key={k} className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{ background: PATCH_KIND_META[k].bg, color: PATCH_KIND_META[k].color }}>
                  {kindCounts[k]} {PATCH_KIND_META[k].label}
                </span>
              ))}
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-1.5 self-center">
            {apps.map(a => {
              const n = rel.entries.filter(e => e.app === a).length
              if (n === 0) return null
              return (
                <span key={a} className="text-[10px] font-bold tracking-wider px-2 py-1 rounded-full"
                      style={{ background: PATCH_APP_META[a].accent + '22', color: PATCH_APP_META[a].accent }}>
                  {PATCH_APP_META[a].emoji} {n}
                </span>
              )
            })}
          </div>
        </div>

        {/* Top-3 Highlight-Items pro App (Kind 'new' bevorzugt) */}
        <div className="grid md:grid-cols-3 gap-3">
          {apps.map(app => {
            const items = rel.entries
              .filter(e => e.app === app)
              .sort((a, b) => PATCH_KIND_ORDER.indexOf(a.kind) - PATCH_KIND_ORDER.indexOf(b.kind))
              .slice(0, 3)
            if (items.length === 0) return null
            const meta = PATCH_APP_META[app]
            return (
              <div key={app} className="rounded-2xl border p-4" style={{ borderColor: 'var(--border)', background: 'var(--bg-elev)' }}>
                <div className="flex items-center gap-2 mb-2.5 pb-2 border-b" style={{ borderColor: 'var(--border)' }}>
                  <span className="text-base">{meta.emoji}</span>
                  <span className="text-xs font-bold">{meta.short}</span>
                </div>
                <ul className="space-y-2">
                  {items.map((e, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="shrink-0 w-1 self-stretch rounded-full mt-0.5"
                            style={{ background: PATCH_KIND_META[e.kind].color }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold tracking-wider"
                             style={{ color: PATCH_KIND_META[e.kind].color }}>
                          {PATCH_KIND_META[e.kind].label}
                        </div>
                        <div className="text-sm font-medium leading-snug">{e.title}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/patchnotes"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm border-2 hover:bg-accent/10 transition"
            style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
          >
            Alle Patchnotes ansehen
            <span>→</span>
          </Link>
        </div>
      </div>
    </section>
  )
}

// ── ROADMAP ──────────────────────────────────────────────────────────────
//  Holt Items dynamisch aus /api/v1/roadmap (Admin-pflegbar).
//  Fällt auf statische Liste zurück, falls Worker nicht erreichbar.
type RoadmapStatus = 'done' | 'now' | 'next' | 'later'
interface RoadmapItem {
  id?: number
  quarter: string
  title: string
  status: RoadmapStatus
  description: string
}

const FALLBACK_ROADMAP: RoadmapItem[] = [
  { quarter: 'Live',    title: 'iOS · iPadOS · v1.1',       status: 'done',  description: 'v1.1 im App Store mit komplettem Redesign, neuem Prism-Skin und 2× schnellerem Stempel-Status. iPhone + iPad voll production-ready.' },
  { quarter: 'Bald',    title: 'macOS (in Apple-Review)',   status: 'now',   description: 'Mac-Version derzeit bei Apple zur Prüfung — Freigabe binnen weniger Tage erwartet.' },
  { quarter: 'Q3',      title: 'Webapp (du bist hier!)',    status: 'now',   description: 'Browser-Version mit Login, Dashboard, Chat. Kein Download nötig.' },
  { quarter: 'Q4',      title: 'Android (Kotlin)',          status: 'next',  description: 'Native Android-App, gleiche API, gleiche Features wie iOS.' },
  { quarter: '2027',    title: 'watchOS Companion',         status: 'later', description: 'Stempeluhr direkt von der Apple Watch.' },
  { quarter: '2027',    title: 'Slack/Teams Integration',   status: 'later', description: 'Notifications + Bot-Commands für Cross-Plattform-Teams.' },
  { quarter: 'Vision',  title: 'visionOS Spatial-Workflow', status: 'later', description: 'Räumliche Mitarbeiter-Karten, immersive Stats.' },
]

function RoadmapSection() {
  const [items, setItems] = useState<RoadmapItem[]>(FALLBACK_ROADMAP)

  useEffect(() => {
    fetch(`${API_ORIGIN}/api/v1/roadmap`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (j?.items?.length) {
          // Worker liefert {id, quarter, title, description, status, sort_order, ...}
          setItems(j.items.map((x: any) => ({
            id:          x.id,
            quarter:     x.quarter ?? '',
            title:       x.title ?? '',
            status:      (x.status ?? 'later') as RoadmapStatus,
            description: x.description ?? '',
          })))
        }
      })
      .catch(() => { /* Fallback bleibt */ })
  }, [])

  const dot = (s: RoadmapStatus) =>
    s === 'done'  ? 'bg-teal'      :
    s === 'now'   ? 'bg-accent animate-pulse-glow' :
    s === 'next'  ? 'bg-brandBlue' :
                    'bg-app-text-mute'
  const label = (s: RoadmapStatus) =>
    s === 'done'  ? '✓ LIVE'   :
    s === 'now'   ? '▶ JETZT'  :
    s === 'next'  ? '→ NÄCHSTES' :
                    '… GEPLANT'

  return (
    <section id="roadmap" className="px-6 pb-24 max-w-6xl mx-auto">
      <div className="text-center mb-14">
        <div className="inline-block text-xs font-bold tracking-wider text-accent">ROADMAP</div>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold heading-fade">Was läuft. Was kommt.</h2>
      </div>
      <div className="glass-strong p-6 md:p-10">
        <div className="space-y-1">
          {items.map((item, i) => (
            <div key={item.id ?? i} className="flex items-start gap-4 py-4 border-b border-app-border last:border-b-0">
              <div className="flex flex-col items-center pt-1">
                <div className={`w-3 h-3 rounded-full ${dot(item.status)}`} />
                {i < items.length - 1 && <div className="w-px h-12 bg-app-border mt-1" />}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                  <span className="text-[10px] font-bold tracking-wider text-app-text-mute">{item.quarter}</span>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                    item.status === 'done'  ? 'bg-teal/15 text-teal'           :
                    item.status === 'now'   ? 'bg-accent/15 text-accent'       :
                    item.status === 'next'  ? 'bg-brandBlue/15 text-brandBlue' :
                                              'bg-app-elev text-app-text-mute'
                  }`}>{label(item.status)}</span>
                </div>
                <h4 className="text-lg font-bold text-app-text">{item.title}</h4>
                <p className="text-sm text-app-text-dim mt-1 leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── BETA TESTER SECTION ──────────────────────────────────────────────────
function BetaTesterSection() {
  const [count,   setCount]   = useState<number | null>(null)
  const [limit,   setLimit]   = useState(100)
  const [email,   setEmail]   = useState('')
  const [name,    setName]    = useState('')
  const [device,  setDevice]  = useState('multiple')
  const [team,    setTeam]    = useState('1')
  const [msg,     setMsg]     = useState('')
  const [busy,    setBusy]    = useState(false)
  const [done,    setDone]    = useState(false)
  const [err,     setErr]     = useState<string | null>(null)

  useEffect(() => {
    fetch(`${API_ORIGIN}/beta-count`)
      .then(r => r.json())
      .then(j => {
        setCount(j.count ?? 0)
        setLimit(j.limit ?? 100)
      })
      .catch(() => setCount(0))
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      const res = await fetch(`${API_ORIGIN}/beta-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          firstName: name.trim() || undefined,
          device,
          teamSize: team,
          message: msg.trim() || undefined,
        }),
      })
      const json = await res.json()
      if (!res.ok) {
        setErr(json.error ?? 'Anmeldung fehlgeschlagen.')
      } else {
        setDone(true)
        if (typeof json.totalSignups === 'number') setCount(json.totalSignups)
      }
    } catch (e: any) {
      setErr(e?.message ?? 'Netzwerkfehler.')
    } finally {
      setBusy(false)
    }
  }

  const slotsLeft = count !== null ? Math.max(0, limit - count) : null
  const pct = count !== null ? Math.min(100, (count / limit) * 100) : 0

  return (
    <section id="beta" className="px-6 pb-24 max-w-5xl mx-auto">
      <div className="glass-strong p-8 md:p-14 relative overflow-hidden">
        {/* Floating Glow im Hintergrund */}
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-accent/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-brandBlue/25 blur-3xl pointer-events-none" />

        <div className="relative">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/15 border border-accent/30 text-xs font-bold tracking-wider text-accent mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              BETA-TESTER GESUCHT
            </div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              <span className="heading-fade">
                100 Plätze.<br/>Lebenslang dabei.
              </span>
            </h2>
            <p className="mt-5 text-app-text-dim max-w-xl mx-auto leading-relaxed">
              Bewirb dich als Tester für kommende Builds und Projekte —
              CustoSoft, die Webapp, die Android-Version, watchOS, alles.
              Wer dabei ist, behält <strong className="text-app-text">alle Inhalte zum Zeitpunkt des Test-Endes lebenslang</strong> —
              ohne weitere Updates, aber gratis für immer.
            </p>
          </div>

          {/* Progress + Counter */}
          <div className="mb-10 max-w-md mx-auto">
            <div className="flex justify-between items-baseline mb-2 text-sm">
              <span className="text-app-text-dim">
                {count !== null ? (
                  <>
                    <span className="font-bold text-app-text">{count}</span> von {limit} Plätzen vergeben
                  </>
                ) : 'Lade…'}
              </span>
              <span className="font-bold text-accent">
                {slotsLeft !== null && slotsLeft > 0 ? `${slotsLeft} frei` : 'Voll'}
              </span>
            </div>
            <div className="h-3 rounded-full bg-app-border overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-accent via-brandBlue to-teal transition-all duration-1000"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Belohnungs-Karten */}
          <div className="grid md:grid-cols-3 gap-3 mb-10">
            <RewardTile icon="🔓" title="Lebenslanger Zugang"   text="Solange du testest: alle Features freigeschaltet." />
            <RewardTile icon="💎" title="Frozen Forever"         text="Beim Test-Ende behältst du alles was du hast — gratis, dauerhaft." />
            <RewardTile icon="🚀" title="Frühzugriff"            text="Erstes Auge auf Webapp, Android, watchOS, Spatial — alles." />
          </div>

          {/* Form oder Erfolgs-Banner */}
          {done ? (
            <div className="glass p-8 text-center">
              <div className="text-5xl mb-3">🎉</div>
              <h3 className="text-2xl font-bold mb-2 text-app-text">Du bist drin!</h3>
              <p className="text-app-text-dim max-w-md mx-auto">
                Bestätigungs-Mail unterwegs. Wir melden uns sobald der nächste Beta-Slot offen ist.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <FormField label="Vorname (optional)" value={name} onChange={setName} placeholder="Anna" />
              <FormField label="E-Mail" type="email" value={email} onChange={setEmail} placeholder="anna@example.com" required />

              <SelectField label="Gerät" value={device} onChange={setDevice} options={[
                { v: 'iphone',   l: 'iPhone' },
                { v: 'ipad',     l: 'iPad' },
                { v: 'mac',      l: 'Mac' },
                { v: 'multiple', l: 'Mehrere Geräte' },
                { v: 'android',  l: 'Android (für später)' },
              ]} />
              <SelectField label="Team-Größe" value={team} onChange={setTeam} options={[
                { v: '1',     l: 'Alleine' },
                { v: '2-5',   l: '2–5 Personen' },
                { v: '6-20',  l: '6–20 Personen' },
                { v: '20+',   l: '20+ Personen' },
              ]} />

              <div className="md:col-span-2">
                <FormField label="Nachricht (optional) — Was nutzt du, was vermisst du?" value={msg} onChange={setMsg} multiline />
              </div>

              {err && (
                <div className="md:col-span-2 text-sm text-coral bg-coral/10 border border-coral/30 rounded-xl p-3">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || !email}
                className="md:col-span-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-base py-4"
              >
                {busy ? 'Sende…' : (slotsLeft === 0 ? 'Auf die Warteliste' : 'Mich auf Tester-Liste setzen')}
              </button>

              <p className="md:col-span-2 text-[11px] text-app-text-mute text-center leading-relaxed">
                Mit dem Absenden stimmst du zu dass wir deine E-Mail nutzen um dich
                zu Beta-Programmen einzuladen. Kein Spam, keine Weitergabe an Dritte.
                Jederzeit Abbestellbar.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}

function RewardTile({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="glass p-5 tilt-hover">
      <div className="text-3xl mb-2">{icon}</div>
      <h4 className="font-bold mb-1 text-app-text">{title}</h4>
      <p className="text-xs text-app-text-dim leading-relaxed">{text}</p>
    </div>
  )
}

function FormField(props: {
  label: string; value: string; onChange: (s: string) => void;
  type?: string; placeholder?: string; required?: boolean; multiline?: boolean
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-app-text-dim mb-2 tracking-wider uppercase">{props.label}</span>
      {props.multiline ? (
        <textarea
          value={props.value} onChange={e => props.onChange(e.target.value)}
          placeholder={props.placeholder} rows={3}
          className="input-base"
        />
      ) : (
        <input
          type={props.type ?? 'text'}
          value={props.value} onChange={e => props.onChange(e.target.value)}
          placeholder={props.placeholder} required={props.required}
          className="input-base"
        />
      )}
    </label>
  )
}

function SelectField(props: {
  label: string; value: string; onChange: (s: string) => void;
  options: { v: string; l: string }[]
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-app-text-dim mb-2 tracking-wider uppercase">{props.label}</span>
      <select
        value={props.value} onChange={e => props.onChange(e.target.value)}
        className="input-base"
      >
        {props.options.map(o => (
          <option key={o.v} value={o.v} className="bg-app-bg text-app-text">{o.l}</option>
        ))}
      </select>
    </label>
  )
}

// ── SERVER TEASER (verlinkt auf /server) ────────────────────────────────
function ServerTeaser() {
  return (
    <section className="relative px-6 py-20 max-w-6xl mx-auto">
      <div className="relative overflow-hidden rounded-3xl p-8 md:p-12"
           style={{
             background: 'linear-gradient(135deg, rgba(138,68,238,0.18), rgba(58,98,255,0.10))',
             border: '2px solid rgba(138,68,238,0.40)',
           }}>
        {/* Animierter Mesh-BG */}
        <div className="absolute inset-0 mesh-bg opacity-50 pointer-events-none" />
        {/* Floating Orb */}
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full pointer-events-none animate-float-orb-1"
             style={{ background: 'radial-gradient(circle, rgba(138,68,238,0.40), transparent 70%)' }} />

        <div className="relative grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest mb-4"
                 style={{ background: 'rgba(138,68,238,0.25)', color: '#c084fc', border: '1px solid rgba(138,68,238,0.40)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
              NEU · ON-PREMISE-EDITION
            </div>
            <h2 className="text-3xl md:text-5xl font-bold heading-fade leading-tight mb-4">
              Lieber auf eigenem Server?<br/>CustoSoft Server.
            </h2>
            <p className="text-app-text-dim text-base mb-6 leading-relaxed">
              Komplette Team-Suite auf <strong className="text-app-text">Ihrem Server</strong> —
              keine Cloud, kein Abo. Stempeluhr · Zeitenübersicht · Schichtplan · Akten ·
              Krankmeldungen · Bewerbungen. Selbst gehostet, einmal bezahlt.
            </p>
            <ul className="grid grid-cols-2 gap-2 text-sm text-app-text-dim mb-7">
              <li className="flex items-center gap-2"><span className="text-green-400">✓</span> ab 1.990 € einmalig</li>
              <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Selbst-Install ~ 2–8 h</li>
              <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Stunden-CSV-Export</li>
              <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Keine Lohn-Berechnung</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/server"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white text-sm mega-glow"
                style={{ background: 'linear-gradient(135deg, #8a44ee, #3a62ff)' }}
              >
                Mehr erfahren →
              </Link>
              <a
                href="mailto:server@custosoft.de?subject=Demo-Termin%20CustoSoft%20Server"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-app-text border-2 border-app-border hover:bg-app-elev-2/60 transition text-sm"
              >
                ✉ Demo anfragen
              </a>
            </div>
          </div>

          {/* Rechte Seite: Deployment-Format-Stack */}
          <div className="relative hidden md:flex flex-col gap-3">
            {[
              { ico: '🐳', t: 'Docker · Compose',     v: 'docker compose up -d' },
              { ico: '🐧', t: 'Linux · .deb / .rpm',  v: 'apt install custosoft-server' },
              { ico: '🪟', t: 'Windows · MSI',        v: 'CustoSoftServer.msi' },
              { ico: '🍎', t: 'macOS · PKG',          v: 'CustoSoftServer.pkg' },
              { ico: '📦', t: 'Standalone-Binary',    v: '~80 MB single file' },
            ].map((d, i) => (
              <div key={d.t}
                   className="flex items-center gap-3 px-4 py-3 rounded-xl backdrop-blur-md animate-float-card"
                   style={{
                     background: 'rgba(0,0,0,0.30)',
                     border: '1px solid rgba(255,255,255,0.10)',
                     animationDelay: (i * 0.4) + 's',
                   }}>
                <span className="text-2xl">{d.ico}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">{d.t}</div>
                  <div className="text-[11px] font-mono text-accent truncate">{d.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ── FINAL CTA ────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="px-6 pb-24 max-w-4xl mx-auto text-center">
      <h2 className="text-4xl md:text-5xl font-bold leading-tight">
        <span className="heading-fade-soft">
          Bereit?
        </span>
      </h2>
      <p className="mt-4 text-app-text-dim">In zwei Minuten loslegen — App laden oder direkt im Browser.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="btn-primary text-base px-8 py-4">
          <AppleLogo />
          Im App Store
        </a>
        <Link to="/login" className="btn-ghost text-base px-8 py-4">
          Web-App öffnen
        </Link>
      </div>
    </section>
  )
}

// ── FOOTER ───────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="px-6 pb-12 pt-12 border-t border-app-border max-w-6xl mx-auto">
      <div className="flex flex-wrap justify-between items-center gap-6">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent to-accent2 grid place-items-center font-bold text-white text-sm">C</span>
          <span className="font-bold tracking-tight text-app-text">CustoSoft</span>
          <span className="text-xs text-app-text-mute ml-2">© 2026</span>
        </div>
        <div className="flex flex-wrap gap-5 text-xs text-app-text-mute">
          {/* Rechtstexte werden direkt vom Worker gerendert (lokalisiert: de/en).
             Alte URLs (custosoftcustomers.com) sind abgelöst. */}
          <a className="hover:text-app-text transition" href="https://custosoft-api.davidschroedinger.workers.dev/impressum">Impressum</a>
          <a className="hover:text-app-text transition" href="https://custosoft-api.davidschroedinger.workers.dev/datenschutz">Datenschutz</a>
          <a className="hover:text-app-text transition" href="https://custosoft-api.davidschroedinger.workers.dev/agb">AGB</a>
          <a className="hover:text-app-text transition" href="https://custosoft-api.davidschroedinger.workers.dev/widerruf">Widerruf</a>
          <a className="hover:text-app-text transition" href="mailto:taironic.media@gmail.com">Kontakt</a>
        </div>
      </div>
      <p className="mt-6 text-[11px] text-app-text-mute text-center">
        CustoSoft · David Schrödinger · Made with 🔥 in Germany
      </p>
    </footer>
  )
}

// ── Apple Logo SVG ───────────────────────────────────────────────────────
function AppleLogo() {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.6 8.5c0-1.7 1.4-2.5 1.5-2.6-.8-1.2-2.1-1.4-2.5-1.4-1.1-.1-2.1.6-2.7.6S6.6 4.4 5.6 4.5c-1.2 0-2.3.7-2.9 1.7-1.2 2.2-.3 5.4.9 7.2.6.9 1.3 1.8 2.2 1.8.9 0 1.2-.6 2.3-.6s1.4.6 2.3.6c1 0 1.6-.9 2.2-1.8.7-1 1-2.1 1-2.1 0 0-1.9-.7-2-2.8zM9.7 3c.5-.6.8-1.4.7-2.3-.7 0-1.6.5-2.1 1.1-.4.5-.8 1.4-.7 2.2.8.1 1.6-.4 2.1-1z"/>
    </svg>
  )
}
